import React from 'react';
import { handleRedirect, acquireGraphAccessToken } from '../auth/msal';
import type { Account, AccountState, Profile } from '@shared/types/account';
import {
  errorTelemetryProperties,
  getTelemetryCorrelationHeaders,
  trackException,
  trackFunnelEvent,
} from '@/telemetry';

type SessionStatus = 'checking' | 'unauth' | 'auth';

export type SessionValue = {
  status: SessionStatus;
  profile: Profile | null;
  account: Account | null;
  accountState: AccountState | null;
  autoActivateEnabled: boolean;
  isSignedup: boolean;
  isWaitlisted: boolean;
  isRegistered: boolean;
  isProvisioning: boolean;
  isActive: boolean;
  isInactive: boolean;
  isTerminated: boolean;
  isAppEligible: boolean;
  isSystemAdmin: boolean;
  hasPhone: boolean;
  hasBilling: boolean;
  refresh: () => Promise<void>;
  markRegistered: (account: Account) => void;
  updateAccount: (account: Account | null) => void;
};

const SessionContext = React.createContext<SessionValue | undefined>(undefined);

function deriveAccountState(acc: Account | null): AccountState | null {
  if (!acc) return null;
  if (acc.state === 'signedup'
    || acc.state === 'waitlisted'
    || acc.state === 'registered'
    || acc.state === 'provisioning'
    || acc.state === 'active'
    || acc.state === 'inactive'
    || acc.state === 'terminated') return acc.state;
  if (acc.state === 'signedUp') return 'signedup';
  if (acc.businessInfo) return 'waitlisted';
  return 'signedup';
}

function deriveHasBilling(acc: Account | null): boolean {
  if (!acc?.billing) return false;
  return acc.billing.status === 'active' && Boolean(acc.billing.subscriptionId);
}

function deriveNextRoute(acc: Account | null): string {
  const accountState = deriveAccountState(acc);
  if (accountState === 'active' || accountState === 'inactive') return '/app';
  if (accountState === 'registered' || accountState === 'provisioning') return '/billing';
  return '/completeSignup';
}

function hasMsalRedirectResponse(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search || window.location.hash.replace(/^#/, ''));
  return params.has('code') || params.has('state') || params.has('error') || params.has('id_token');
}

function headersWithTelemetry(headers: HeadersInit = {}): Headers {
  const nextHeaders = new Headers(headers);
  for (const [key, value] of Object.entries(getTelemetryCorrelationHeaders())) {
    nextHeaders.set(key, value);
  }
  return nextHeaders;
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<SessionStatus>('checking');
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [account, setAccount] = React.useState<Account | null>(null);
  const [accountState, setAccountState] = React.useState<AccountState | null>(null);
  const [autoActivateEnabled, setAutoActivateEnabled] = React.useState(true);
  const [hasPhone, setHasPhone] = React.useState(false);
  const [hasBilling, setHasBilling] = React.useState(false);
  const bootstrappedRef = React.useRef(false);

  const applyAccount = React.useCallback((nextAccount: Account | null) => {
    setAccount(nextAccount);
    const derivedState = deriveAccountState(nextAccount);
    setAccountState(derivedState);
    setHasPhone(Boolean(nextAccount?.phoneNumber));
    setHasBilling(deriveHasBilling(nextAccount));
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const me = await fetch('/api/me', {
        credentials: 'include',
        cache: 'no-store',
        headers: headersWithTelemetry(),
      });
      if (me.status !== 200) {
        setStatus('unauth');
        setProfile(null);
        applyAccount(null);
        setAutoActivateEnabled(true);
        return;
      }
      const meData = await me.json().catch(() => ({}));
      setProfile(meData.profile || null);

      const accResp = await fetch('/api/accounts/me', {
        credentials: 'include',
        cache: 'no-store',
        headers: headersWithTelemetry(),
      });
      if (accResp.ok) {
        const accData = await accResp.json().catch(() => ({}));
        const acc = (accData.account || null) as Account | null;
        applyAccount(acc);
        setAutoActivateEnabled(typeof accData.autoActivateEnabled === 'boolean' ? accData.autoActivateEnabled : true);
        trackFunnelEvent('account_state_resolved', {
          accountState: deriveAccountState(acc) || 'unknown',
          nextRoute: deriveNextRoute(acc),
        });
      } else {
        applyAccount(null);
        setAutoActivateEnabled(true);
      }
      setStatus('auth');
    } catch {
      setStatus('unauth');
      setProfile(null);
      applyAccount(null);
      setAutoActivateEnabled(true);
    }
  }, [applyAccount]);

  const updateAccount = React.useCallback((nextAccount: Account | null) => {
    applyAccount(nextAccount);
    if (nextAccount) setStatus('auth');
  }, [applyAccount]);

  const markRegistered = React.useCallback((registeredAccount: Account) => {
    updateAccount(registeredAccount);
  }, [updateAccount]);

  React.useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    (async () => {
      setStatus('checking');
      let redirectReturned = false;
      let callbackAttempted = false;
      try {
        redirectReturned = hasMsalRedirectResponse();
        if (redirectReturned) {
          trackFunnelEvent('auth_redirect_returned');
        }
        const result = await handleRedirect();
        if (result?.idToken) {
          const graphAccessToken = result.accessToken || (await acquireGraphAccessToken());
          callbackAttempted = true;
          trackFunnelEvent('auth_callback_started');
          const callbackResp = await fetch('/api/auth/callback', {
            method: 'POST',
            headers: headersWithTelemetry({ 'Content-Type': 'application/json' }),
            credentials: 'include',
            body: JSON.stringify({ idToken: result.idToken, graphAccessToken }),
          });
          if (!callbackResp.ok) {
            trackFunnelEvent('auth_callback_failed', { statusCode: callbackResp.status });
            setStatus('unauth');
            return;
          }
          trackFunnelEvent('auth_callback_succeeded');
        }

        await refresh();
      } catch (err) {
        if (redirectReturned || callbackAttempted) {
          trackFunnelEvent('auth_callback_failed', errorTelemetryProperties(err));
          trackException(err, { eventName: 'auth_callback_failed' });
        }
        setStatus('unauth');
        setProfile(null);
        applyAccount(null);
      }
    })();
  }, [refresh, applyAccount]);

  const value = React.useMemo<SessionValue>(() => ({
    status,
    profile,
    account,
    accountState,
    autoActivateEnabled,
    isSignedup: accountState === 'signedup',
    isWaitlisted: accountState === 'waitlisted',
    isRegistered: accountState === 'registered',
    isProvisioning: accountState === 'provisioning',
    isActive: accountState === 'active',
    isInactive: accountState === 'inactive',
    isTerminated: accountState === 'terminated',
    isAppEligible: accountState === 'active' || accountState === 'inactive',
    isSystemAdmin: Boolean(account?.isSystemAdmin),
    hasPhone,
    hasBilling,
    refresh,
    markRegistered,
    updateAccount,
  }), [status, profile, account, accountState, autoActivateEnabled, hasPhone, hasBilling, refresh, markRegistered, updateAccount]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionInternal() {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
